import React, { useEffect, useState } from "react";
import {
  Box, TextField, Button, Typography, Grid,
  Paper, Alert, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

const AddUser = () => {
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", mobile: "", email: "",
    company: "", userId: "", password: "", confirmPassword: "",
    bankName: "", accountNumber: "", ifsc: "", upi: "", role: ""
  });

  const [userIdExists, setUserIdExists] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);

  // Auto-generate userId from mobile
  useEffect(() => {
    if (formData.mobile.length >= 6) {
      const generatedId = `CB${formData.mobile.slice(0, 6)}`;
      setFormData((prev) => ({ ...prev, userId: generatedId }));

      fetch(`https://core-bankers-backend.vercel.app/check-userid/${generatedId}`)
        .then((res) => res.json())
        .then((data) => setUserIdExists(data.exists))
        .catch((err) => console.error("User ID check failed:", err));
    }
  }, [formData.mobile]);

  // Fetch matching companies for autocomplete
  useEffect(() => {
    const fetchCompanies = async () => {
      if (formData.company.length < 2) return;
      try {
        const res = await fetch(`https://core-bankers-backend.vercel.app/search-companies?q=${formData.company}`);
        const data = await res.json();
        setCompanyOptions(data.suggestions || []);
      } catch (err) {
        console.error("Company autocomplete error:", err);
      }
    };
    fetchCompanies();
  }, [formData.company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "password" || name === "confirmPassword") {
      setPasswordMismatch(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (formData.password !== formData.confirmPassword) {
      setPasswordMismatch(true);
      return;
    }

    if (userIdExists) {
      setErrorMessage("User ID already exists. Try a different mobile number.");
      return;
    }

    fetch("https://core-bankers-backend.vercel.app/add-user", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData)
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to add user");
        return res.json();
      })
      .then(() => {
        setSuccessMessage("User added successfully!");
        setFormData({
          firstName: "", lastName: "", mobile: "", email: "",
          company: "", userId: "", password: "", confirmPassword: "",
          bankName: "", accountNumber: "", ifsc: "", upi: "", role: ""
        });
        setCompanyOptions([]);
      })
      .catch((err) => {
        console.error("Add user error:", err);
        setErrorMessage("An error occurred while adding user.");
      });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, maxWidth: 700, mx: "auto" }}>
        <Typography variant="h5" gutterBottom>Add New User</Typography>

        {passwordMismatch && <Alert severity="error">Passwords do not match.</Alert>}
        {userIdExists && <Alert severity="error">User ID already exists.</Alert>}
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}

        <form onSubmit={handleSubmit}>
          <Typography variant="subtitle1" gutterBottom mt={2}>Personal Info</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Mobile Number" name="mobile" value={formData.mobile} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email ID" name="email" value={formData.email} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={companyOptions}
                inputValue={formData.company}
                onInputChange={(e, value) => setFormData(prev => ({ ...prev, company: value }))}
                renderInput={(params) => (
                  <TextField {...params} label="Current Company" fullWidth />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom mt={4}>Login Credentials</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="User ID" name="userId" value={formData.userId} onChange={handleChange} fullWidth required disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="password" label="Password" name="password" value={formData.password} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="password" label="Confirm Password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>User Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                >
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                  <MenuItem value="executive">Financer</MenuItem>
                  <MenuItem value="sub_dsa">Sub DSA</MenuItem>
                  <MenuItem value="cb_executive">CB Executive</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom mt={4}>Bank Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Account Number" name="accountNumber" value={formData.accountNumber} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="IFSC" name="ifsc" value={formData.ifsc} onChange={handleChange} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="UPI ID" name="upi" value={formData.upi} onChange={handleChange} fullWidth />
            </Grid>
          </Grid>

          <Box mt={4} textAlign="center">
            <Button type="submit" variant="contained" sx={{ px: 6 }}>
              Submit
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default AddUser;